if (!customElements.get('frame-addon')) {
  customElements.define(
    'frame-addon',
    class FrameAddon extends HTMLElement {
      constructor() {
        super();

        this.config = JSON.parse(this.querySelector('[data-frame-config]').textContent);
        this.checkbox = this.querySelector('.frame-addon__checkbox');
        this.label = this.querySelector('[data-frame-label]');
        this.colorGroup = this.querySelector('.frame-addon__color-group');
        this.colorList = this.querySelector('[data-frame-color-list]');

        this.posterSizeIdx = parseInt(this.dataset.posterSizeIdx, 10);
        this.sectionId = this.dataset.sectionId;
        this.form = document.getElementById(this.dataset.productFormId);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Normalize size strings: replace non-breaking spaces ( ) with
        // regular spaces so that poster option values entered via copy-paste
        // (which can contain NBSP) still match frame variant size strings.
        this.sizeMap = (this.config.variants || []).reduce((acc, v) => {
          const key = this.normalizeSize(v.size);
          if (!acc[key]) acc[key] = [];
          acc[key].push(v);
          return acc;
        }, {});

        this.matchedVariants = [];
        this.selectedFrameVariant = null;

        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onColorChange = this.onColorChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onPosterVariantChange = this.onPosterVariantChange.bind(this);
      }

      connectedCallback() {
        this.checkbox.checked = Boolean(this.config.checkedByDefault);

        this.checkbox.addEventListener('change', this.onCheckboxChange);
        this.colorList.addEventListener('change', this.onColorChange);
        // Register submit listener on document (capture phase) so we
        // fire before product-form.js handler attached on the form node.
        this.documentSubmitListener = (event) => {
          if (event.target === this.form) this.onSubmit(event);
        };
        document.addEventListener('submit', this.documentSubmitListener, true);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onPosterVariantChange);

        const initialSize = this.dataset.initialSize || '';
        this.applySize(initialSize);
      }

      disconnectedCallback() {
        this.checkbox.removeEventListener('change', this.onCheckboxChange);
        this.colorList.removeEventListener('change', this.onColorChange);
        if (this.documentSubmitListener) {
          document.removeEventListener('submit', this.documentSubmitListener, true);
        }
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onPosterVariantChange(event) {
        if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
        const variant = event.data.variant;
        if (!variant) {
          this.applySize('');
          return;
        }
        const sizeValue = this.extractOption(variant, this.posterSizeIdx);
        this.applySize(sizeValue);
      }

      extractOption(variant, idx) {
        if (idx === 0) return variant.option1 || '';
        if (idx === 1) return variant.option2 || '';
        if (idx === 2) return variant.option3 || '';
        return '';
      }

      normalizeSize(str) {
        return (str || '').replace(/ /g, ' ').trim();
      }

      applySize(sizeValue) {
        const matches = (sizeValue && this.sizeMap[this.normalizeSize(sizeValue)]) || [];
        this.matchedVariants = matches.filter((v) => v.available);

        if (this.matchedVariants.length === 0) {
          this.hidden = true;
          this.checkbox.checked = false;
          this.selectedFrameVariant = null;
          this.refreshLabel();
          return;
        }

        this.hidden = false;
        // Preselect the first color when the checkbox is on (default or
        // toggled by the user). Otherwise keep selection empty so no
        // chip is highlighted.
        this.selectedFrameVariant = this.checkbox.checked ? this.matchedVariants[0] : null;
        this.renderColorOptions();
        this.refreshLabel();
      }

      renderColorOptions() {
        const fragments = this.matchedVariants
          .map((v) => {
            const isSelected = this.selectedFrameVariant != null && v.id === this.selectedFrameVariant.id;
            const hex = this.colorHexFor(v.color);
            const inputId = `FrameAddonColor-${this.sectionId}-${v.id}`;
            const hasImage = Boolean(v.image);
            let visualHtml;
            if (hasImage) {
              visualHtml = `<img class="frame-addon__color-image" src="${this.escapeHtml(v.image)}" alt="${this.escapeHtml(v.color)}" width="52" height="64" loading="lazy">`;
            } else {
              const styleAttr = hex ? ` style="background-color: ${this.escapeHtml(hex)};"` : '';
              const fallbackClass = hex ? '' : ' frame-addon__color-swatch--fallback';
              visualHtml = `<span class="frame-addon__color-swatch${fallbackClass}"${styleAttr} aria-hidden="true"></span>`;
            }
            return `
              <label
                class="frame-addon__color-option${isSelected ? ' frame-addon__color-option--selected' : ''}"
                for="${inputId}"
              >
                <input
                  type="radio"
                  class="frame-addon__color-radio"
                  id="${inputId}"
                  name="frame-addon-color-${this.sectionId}"
                  value="${v.id}"
                  ${isSelected ? 'checked' : ''}
                >
                <span class="frame-addon__color-chip">
                  ${visualHtml}
                </span>
                <span class="frame-addon__color-name">${this.escapeHtml(v.color)}</span>
              </label>
            `;
          })
          .join('');
        this.colorList.innerHTML = fragments;
      }

      colorHexFor(name) {
        const swatches = this.config.colorSwatches || {};
        return swatches[name] || '';
      }

      escapeHtml(s) {
        return String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      onCheckboxChange() {
        if (this.checkbox.checked) {
          // Checkbox just turned on: preselect the first available color.
          if (!this.selectedFrameVariant && this.matchedVariants.length > 0) {
            this.selectedFrameVariant = this.matchedVariants[0];
          }
        } else {
          // Checkbox just turned off: clear the selection so no chip is
          // highlighted and the price disappears from the label.
          this.selectedFrameVariant = null;
        }
        this.renderColorOptions();
        this.refreshLabel();
      }

      onColorChange(event) {
        const variantId = parseInt(event.target.value, 10);
        const next = this.matchedVariants.find((v) => v.id === variantId);
        if (!next) return;
        this.selectedFrameVariant = next;
        // Selecting a color implicitly opts the customer into the add-on,
        // mirroring the inverse interaction on the toggle.
        if (!this.checkbox.checked) this.checkbox.checked = true;
        this.colorList.querySelectorAll('.frame-addon__color-option').forEach((el) => {
          el.classList.toggle(
            'frame-addon__color-option--selected',
            el.querySelector('input').value === String(variantId)
          );
        });
        this.refreshLabel();
      }

      refreshLabel() {
        if (this.checkbox.checked && this.selectedFrameVariant) {
          const html = (this.config.labelHtmlWithPrice || '').replace(
            this.config.pricePlaceholder,
            this.escapeHtml(this.selectedFrameVariant.priceFormatted)
          );
          this.label.innerHTML = html;
          return;
        }
        this.label.innerHTML = this.config.labelHtmlWithoutPrice || '';
      }

      onSubmit(event) {
        if (!this.checkbox.checked || !this.selectedFrameVariant || this.hidden) return;
        if (event.defaultPrevented) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const submitButton = this.form.querySelector('[type="submit"]');
        const spinner = this.form.querySelector('.loading-overlay__spinner');
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
        }
        if (spinner) spinner.classList.remove('hidden');

        const formData = new FormData(this.form);
        const posterVariantId = parseInt(formData.get('id'), 10);
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const uid = this.generateUid();

        const sections = this.cart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : [];

        const body = {
          items: [
            {
              id: posterVariantId,
              quantity,
              properties: { _frame_link_uid: uid, _frame_role: 'parent' },
            },
            {
              id: this.selectedFrameVariant.id,
              quantity,
              properties: { _frame_link_uid: uid, _frame_role: 'child' },
            },
          ],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        };

        if (this.cart && this.cart.setActiveElement) {
          this.cart.setActiveElement(document.activeElement);
        }

        fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'frame-addon',
                productVariantId: posterVariantId,
                errors: response.description,
                message: response.message,
              });
              return;
            }
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'frame-addon',
              productVariantId: posterVariantId,
            });
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            if (!response.key && Array.isArray(response.items) && response.items.length > 0) {
              response.key = response.items[0].key;
            }
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (submitButton) {
              submitButton.classList.remove('loading');
              submitButton.removeAttribute('aria-disabled');
            }
            if (spinner) spinner.classList.add('hidden');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
          });
      }

      generateUid() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
          return window.crypto.randomUUID();
        }
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      }
    }
  );
}
